import { cloneElement, createContext, forwardRef, isValidElement, useCallback, useContext, useEffect, useId, useRef, useState, type ChangeEvent, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { Checkbox as RxCheckbox, Slider as RxSlider, Switch as RxSwitch, Label as RxLabel } from "radix-ui";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../lib/cn";
import { useDocumentLocale } from "../lib/microcopy";
import { useFieldDraft } from "../lib/draft";
import { DraftRestoredNote } from "./draft-note";

const fieldBase =
  "flex w-full rounded-lg border border-input bg-white/80 px-3 py-2 text-sm text-foreground shadow-[inset_0_1px_2px_rgb(43_38_32/0.04)] transition-colors placeholder:text-sand-500 focus-visible:outline-none focus-visible:border-aegold-400 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-danger-500 aria-invalid:ring-danger-500";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, type, ...props }, ref) => (
  <input ref={ref} type={type} className={cn(fieldBase, "h-10 file:border-0 file:bg-transparent file:text-sm file:font-medium", className)} {...props} />
));
Input.displayName = "Input";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Keeps what the officer writes here on this device under this name (per signed-in user, see `DraftOwner`) until the server has it, and
   * brings it back first when the screen is reopened after a lapsed session, a reload or a closed tab (S 2.7). Name the record and the field,
   * e.g. `case:${caseId}:criterion:${key}:note`.
   */
  draft?: string | null;
}

const textareaClass = "min-h-24 resize-y leading-relaxed";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, draft, ...props }, ref) =>
  draft ? <DraftTextarea ref={ref} draft={draft} className={className} {...props} /> : <textarea ref={ref} className={cn(fieldBase, textareaClass, className)} {...props} />,
);
Textarea.displayName = "Textarea";

/** Sets a text field's value as typing would, so the screen's own `onChange` (controlled state or a form library) takes it. */
function typeInto(el: HTMLTextAreaElement | null, value: string, applying: { current: boolean }) {
  if (!el || el.value === value) return;
  applying.current = true;
  try {
    Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set?.call(el, value);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  } finally {
    applying.current = false;
  }
}

const DraftTextarea = forwardRef<HTMLTextAreaElement, TextareaProps & { draft: string }>(({ className, draft, onChange, ...props }, ref) => {
  const own = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
    own.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  const controlled = props.value !== undefined;
  const [typed, setTyped] = useState(() => String(props.value ?? props.defaultValue ?? ""));
  const value = controlled ? String(props.value ?? "") : typed;
  // Only what the officer types is kept; a value the draft itself puts back is not their edit.
  const applying = useRef(false);
  const edited = useRef(false);
  const apply = useCallback((v: string) => typeInto(own.current, v, applying), []);
  const kept = useFieldDraft(draft, value, apply, { enabled: !props.readOnly && !props.disabled, edited });
  return (
    <>
      {kept.restored ? <DraftRestoredNote onDiscard={kept.discard} /> : null}
      <textarea
        ref={setRefs}
        className={cn(fieldBase, textareaClass, className)}
        {...props}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { if (!applying.current) edited.current = true; if (!controlled) setTyped(e.target.value); onChange?.(e); }}
      />
    </>
  );
});
DraftTextarea.displayName = "DraftTextarea";

export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
  <div className="relative">
    <select ref={ref} className={cn(fieldBase, "h-10 appearance-none py-0 pe-9", className)} {...props}>
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-sand-600" />
  </div>
));
NativeSelect.displayName = "NativeSelect";

export const Label = forwardRef<HTMLLabelElement, LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }>(
  ({ className, required, children, ...props }, ref) => (
    <RxLabel.Root ref={ref} className={cn("text-sm font-medium leading-none text-aeblack-900 peer-disabled:cursor-not-allowed peer-disabled:opacity-70", className)} {...props}>
      {children}
      {required ? <span className="ms-1 text-danger-600" aria-hidden>*</span> : null}
    </RxLabel.Root>
  ),
);
Label.displayName = "Label";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  children: ReactNode | ((id: string) => ReactNode);
  /** Right-aligned action or copilot hook (e.g. "Explain this field"). */
  action?: ReactNode;
}

/**
 * The field a control sits in: its id and the id of its label. A control that a `<label for>` cannot name (a rich-text editor is a
 * `contenteditable` element) names itself after the label with `aria-labelledby`.
 */
const FieldLabelContext = createContext<{ id: string; labelId: string } | null>(null);
export const useFieldLabel = () => useContext(FieldLabelContext);

export function Field({ label, hint, error, required, className, children, action }: FieldProps) {
  const generated = useId();
  // The label names the control by id: a single control child gets the field's id unless it brings its own.
  const single = typeof children !== "function" && isValidElement<{ id?: string }>(children) ? children : null;
  const id = single?.props.id ?? generated;
  const noteId = `${generated}-note`;
  const note = error || hint;
  useEffect(() => {
    const control = document.getElementById(id);
    if (!control) return;
    if (note) control.setAttribute("aria-describedby", noteId);
    else if (control.getAttribute("aria-describedby") === noteId) control.removeAttribute("aria-describedby");
    if (error) control.setAttribute("aria-invalid", "true");
    else control.removeAttribute("aria-invalid");
  }, [id, noteId, note, error]);
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label || action ? (
        <div className="flex items-center justify-between gap-2">
          {label ? <Label id={`${generated}-label`} htmlFor={id} required={required}>{label}</Label> : <span />}
          {action}
        </div>
      ) : null}
      <FieldLabelContext.Provider value={label ? { id, labelId: `${generated}-label` } : null}>
        {typeof children === "function" ? children(id) : single && !single.props.id ? cloneElement(single, { id }) : children}
      </FieldLabelContext.Provider>
      {error ? (
        <p id={noteId} role="alert" className="text-xs text-danger-600 animate-fade-in">{error}</p>
      ) : hint ? (
        <p id={noteId} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export const Checkbox = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof RxCheckbox.Root>>(({ className, ...props }, ref) => (
  <RxCheckbox.Root
    ref={ref}
    className={cn(
      "peer size-5 shrink-0 rounded-md border border-sand-400 bg-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-aegold-600 data-[state=checked]:bg-aegold-600 data-[state=checked]:text-white",
      className,
    )}
    {...props}
  >
    <RxCheckbox.Indicator className="grid place-items-center">
      <Check className="size-3.5" strokeWidth={3} />
    </RxCheckbox.Indicator>
  </RxCheckbox.Root>
));
Checkbox.displayName = "Checkbox";

export interface SliderProps extends Omit<React.ComponentPropsWithoutRef<typeof RxSlider.Root>, "dir"> {
  /** Defaults to the document language (Arabic pages slide from the right). */
  dir?: "ltr" | "rtl";
  tone?: "gold" | "sea" | "neutral";
}

const sliderRange = { gold: "bg-aegold-600", sea: "bg-seablue-500", neutral: "bg-sand-500" } as const;
const sliderThumb = { gold: "border-aegold-600", sea: "border-seablue-500", neutral: "border-sand-500" } as const;

/** Radix slider in the Sand palette. Renders one thumb per value; pair it with a numeric input for exact entry. */
export const Slider = forwardRef<HTMLSpanElement, SliderProps>(({ className, dir, tone = "gold", value, defaultValue, ...props }, ref) => {
  const locale = useDocumentLocale();
  const thumbs = value ?? defaultValue ?? [0];
  return (
    <RxSlider.Root
      ref={ref}
      dir={dir ?? (locale === "ar" ? "rtl" : "ltr")}
      value={value}
      defaultValue={defaultValue}
      className={cn("relative flex w-full touch-none select-none items-center py-2 data-[disabled]:opacity-50", className)}
      {...props}
    >
      <RxSlider.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-sand-200">
        <RxSlider.Range className={cn("absolute h-full rounded-full transition-colors", sliderRange[tone])} />
      </RxSlider.Track>
      {thumbs.map((_, i) => (
        <RxSlider.Thumb
          key={i}
          className={cn(
            "block size-4 rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            sliderThumb[tone],
          )}
        />
      ))}
    </RxSlider.Root>
  );
});
Slider.displayName = "Slider";

export const Switch = forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof RxSwitch.Root>>(({ className, ...props }, ref) => (
  <RxSwitch.Root
    ref={ref}
    className={cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-aegold-600 data-[state=unchecked]:bg-sand-300",
      className,
    )}
    {...props}
  >
    <RxSwitch.Thumb className="pointer-events-none block size-5 rounded-full bg-white shadow-md ring-0 transition-transform data-[state=checked]:translate-x-5 rtl:data-[state=checked]:-translate-x-5 data-[state=unchecked]:translate-x-0" />
  </RxSwitch.Root>
));
Switch.displayName = "Switch";
